import { Card, Col, Row, Typography } from "antd";
import { EnvironmentOutlined, PhoneOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";
import { Event, EventResponsibility, CompanyContact, Member } from "@bindeleddet/core/types";
import { useHistory } from "react-router-dom";
import { useViewport } from "@bindeleddet/core/hooks";
import { CompanyLogo } from "@bindeleddet/core/components";
import { useEffect, useState } from "react";
import axios from "axios";
import { formatTimeNorwegian, getDateComponentsNorwegian } from "helpers/dateUtils";
import { ResponsibilityType } from "../CreateOrEditEvent/CreateOrEditEvent";

const { Title, Text } = Typography;

interface Props {
  event: Event;
}

const AdminSingleEventHeader: React.FC<Props> = ({ event }) => {
  const { width } = useViewport();
  const breakpoint = 900;

  const [companyContact, setCompanyContact] = useState<CompanyContact>();
  const [members, setMembers] = useState<Member[]>([]);
  const [responsibilities, setResponsibilities] = useState<EventResponsibility[]>();
  const [ha, setHa] = useState<string>("");
  const [mp, setMp] = useState<string>("");

  const history = useHistory();

  useEffect(() => {
    if (!event || !event?.company_contact_id) return;
    axios
      .get<CompanyContact>(`/companies/${event.company.id}/contact-persons/${event.company_contact_id}/`)
      .then(response => {
        setCompanyContact(response.data);
      });
  }, [event]);

  useEffect(() => {
    axios.get<Member[]>(`/internal/members/`).then(response => {
      setMembers(response.data);
    });
  }, []);

  useEffect(() => {
    if (!event) return;
    axios.get<EventResponsibility[]>(`/events/${event.id}/event-responsibility/`).then(response => {
      setResponsibilities(response.data);
    });
  }, [event]);

  useEffect(() => {
    if (members.length === 0 || !responsibilities) return;
    const MPs = responsibilities
      .filter(r => r.responsibility_type === ResponsibilityType.MEETING_RESPONSIBLE)
      .map(r => {
        const member = members.find(m => m.id === r.bl_member);
        return member ? member.full_name : "Ukjent medlem";
      })
      .join(", ");
    setMp(MPs.length > 0 ? MPs : "Ikke bestemt");
    const HAs = responsibilities
      .filter(r => r.responsibility_type === ResponsibilityType.MAIN_RESPONSIBLE)
      .map(r => {
        const member = members.find(m => m.id === r.bl_member);
        return member ? member.full_name : "Ukjent medlem";
      })
      .join(", ");
    setHa(HAs.length > 0 ? HAs : "Ikke bestemt");
  }, [members, responsibilities]);

  if (!event) return null;

  const getTime = () => {
    return formatTimeNorwegian(event.start);
  };

  const getDate = () => {
    const components = getDateComponentsNorwegian(event.start);
    if (!components) return "Ikke bestemt";
    const monthNames = [
      "januar",
      "februar",
      "mars",
      "april",
      "mai",
      "juni",
      "juli",
      "august",
      "september",
      "oktober",
      "november",
      "desember",
    ];
    return `${components.day}. ${monthNames[components.month]} ${components.year}`;
  };

  return (
    <>
      <Card style={{ width: "100%", position: "relative", alignItems: "end" }}>
        <Row gutter={10}>
          <Col span={width < breakpoint ? 24 : 4}>
            <CompanyLogo
              size="95%"
              companyId={event.company.id}
              src={event.company.logo}
              onClick={() => history.push(`/admin/companies/${event.company.id}/`)}
              style={{ cursor: "pointer" }}
            />
          </Col>
          <Col span={width < breakpoint ? 24 : 12} style={width >= breakpoint ? { paddingTop: "10px" } : {}}>
            <Row>
              <Title style={{ margin: 0 }}>{event.name}</Title>
            </Row>
            <Row gutter={10} style={{ padding: "5px 0" }}>
              <Col>
                <Text>
                  <EnvironmentOutlined />
                  {event.location?.name ?? "Ikke bestemt"}
                </Text>
              </Col>
              <Col>
                <Text>
                  <TeamOutlined />
                  {event.registrations}/{event.seats}
                </Text>
              </Col>
              {event.company_contact_id ? (
                <>
                  <Col>
                    <Text>
                      <UserOutlined />
                      {companyContact?.first_name} {companyContact?.last_name}
                    </Text>
                  </Col>
                  <Col>
                    <Text>
                      <PhoneOutlined />
                      {companyContact?.telephone}
                    </Text>
                  </Col>
                </>
              ) : (
                <Col>
                  <Text>
                    <UserOutlined />
                    Kontakt hos bedriften ikke bestemt
                  </Text>
                </Col>
              )}
            </Row>
          </Col>
          <Col span={width < breakpoint ? 24 : 8}>
            <Row gutter={10}>
              <Col span={8} style={{ textAlign: "right" }}>
                <Text strong>HA:</Text>
              </Col>
              <Col span={16}>
                <Text>{ha}</Text>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col span={8} style={{ textAlign: "right" }}>
                <Text strong>MP:</Text>
              </Col>
              <Col span={16}>
                <Text>{mp}</Text>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col span={8} style={{ textAlign: "right" }}>
                <Text strong>Dato:</Text>
              </Col>
              <Col span={16}>
                <Text>{getDate()}</Text>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col span={8} style={{ textAlign: "right" }}>
                <Text strong>Tid:</Text>
              </Col>
              <Col span={16}>
                <Text>{getTime()}</Text>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default AdminSingleEventHeader;
